import { useState } from "react";
import { TableLoaderSmall } from "../../../components/LoaderC";
import styles from "./TimeTracking.module.css";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatCurrencyDecimal } from "../../../helpers/currency";
import { WEEKLY_REGULAR_LIMIT, calcDayWage, calcWeeklyPay, formatMinutesToHM, isWeekend } from "./weeklyPay";
import { EntryDetailModal, EntryFormModal } from "./EntryModals";
import { EmployeeCell, EmptyState } from "./WeekViewTable";

dayjs.extend(customParseFormat);

const fmtTime = (t) => t ? dayjs(t, 'HH:mm:ss').format('hh:mm A') : '—';

function DayViewRow({ item, date, showOt, onOpenEmployee, setIsModalOpen, setIsNewModalOpen, setSelectedEmp, setSelectedDay, setEditData }) {
    const weekend = isWeekend(date);
    const entry = item?.timeTracking?.find(t => t?.date === date) || { date };
    const pay = calcWeeklyPay(item);
    const day = pay.days[date] || { worked: 0, reg: 0, ot: 0, wtdBefore: 0, wtdAfter: 0 };
    const wtd = weekend ? day.wtdBefore : day.wtdAfter;
    const currency = item?.currency || 'USD';
    const { wageTotal } = calcDayWage(item, day);

    const calcParts = [];
    if (day.reg > 0) calcParts.push(`${formatMinutesToHM(day.reg)} × ${formatCurrencyDecimal(item?.wage || 0, currency)}`);
    if (day.ot > 0) calcParts.push(`${formatMinutesToHM(day.ot)} × ${formatCurrencyDecimal(item?.ot_wage || 0, currency)}`);

    const openAdd = (e) => { e.stopPropagation(); setIsNewModalOpen(true); setSelectedEmp(item); setSelectedDay(entry); setEditData(null); };

    let statusEl = <span className={`${styles.stPill} ${styles.stDone}`}>Complete</span>;
    if (!entry?.checkin_time) {
        statusEl = <button type="button" className={`${styles.addTimeBtn}`} onClick={openAdd}>+ Add time</button>;
    } else if (!entry?.checkout_time) {
        statusEl = dayjs(date).isSame(dayjs(), 'day')
            ? <span className={`${styles.stPill} ${styles.stProg}`}>In progress</span>
            : <span className={`${styles.stPill} ${styles.stLate}`}>No clock-out</span>;
    } else if (entry?.late) {
        statusEl = <span className={`${styles.stPill} ${styles.stLate}`}>Late {entry?.late}</span>;
    }

    const hasEntry = !!entry?.checkin_time;

    return (<tr className={`${styles.tbodyTr} ${hasEntry ? styles.clickRow : ''}`} onClick={() => { if (hasEntry) { setIsModalOpen(true); setSelectedEmp(item); setSelectedDay(entry); } }}>
        <td className={`${styles.tdEc}`}><EmployeeCell item={item} onOpenEmployee={onOpenEmployee} /></td>
        <td className={`${styles.td} ${styles.tdBold}`}>{fmtTime(entry?.checkin_time)}</td>
        <td className={`${styles.td} ${styles.tdBold}`}>
            {entry?.checkout_time ? fmtTime(entry?.checkout_time) + (entry?.is_overnight ? ' (+1d)' : '') : (hasEntry ? <span className={`${styles.textMuted}`}>—</span> : '—')}
        </td>
        <td className={`${styles.td}`}>{formatMinutesToHM(entry?.break_mintes)}</td>
        <td className={`${styles.td} ${styles.tdBold}`}>{formatMinutesToHM(day.reg)}</td>
        <td className={`${styles.td}`}>
            <div className={`${styles.wtdCell}`}>
                <span className={`${styles.wtdVal}`}>{formatMinutesToHM(wtd, '0h')} / 40h</span>
                <span className={`${styles.bar} ${styles.barWtd}`}><span className={`${styles.barFill}`} style={{ width: `${Math.min(100, (wtd / WEEKLY_REGULAR_LIMIT) * 100)}%` }} /></span>
            </div>
        </td>
        {showOt && <td className={`${styles.td} ${styles.tdBold} ${styles.textOt}`}>{day.ot > 0 ? '+' + formatMinutesToHM(day.ot) : '—'}</td>}
        <td className={`${styles.td} ${styles.tdRight}`}>
            <div className={`${styles.wageVal}`}>{wageTotal > 0 ? formatCurrencyDecimal(wageTotal, currency) : '—'}</div>
            <div className={`${styles.wageCalc}`}>{calcParts.length > 0 ? calcParts.join(' + ') : (hasEntry ? '' : 'No shift')}</div>
        </td>
        <td className={`${styles.td}`}>{statusEl}</td>
    </tr>);
}

const DayViewTable = ({ contentLoading, filteredData, selectedDate, setLoading, updateTimeTracking, deleteTimeTracking, onOpenEmployee }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [editData, setEditData] = useState(null);

    const weekend = isWeekend(selectedDate);
    // Weekends are all OT. A weekday only gets an OT column once someone passes 40h.
    const showOt = weekend || filteredData?.some(item => calcWeeklyPay(item).days[selectedDate]?.ot > 0);

    return (<div className={`${styles.tblWrap}`}>
        {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

        {!contentLoading && filteredData?.length > 0 && <table className={`${styles.table}`}>
            <thead>
                <tr>
                    <th className={`${styles.thEc}`}>Employee</th>
                    <th className={`${styles.th}`}>Clock in</th>
                    <th className={`${styles.th}`}>Clock out</th>
                    <th className={`${styles.th}`}>Lunch break</th>
                    <th className={`${styles.th}`}>Regular</th>
                    <th className={`${styles.th}`}>{weekend ? 'Weekday hrs so far (Fri–Thu)' : 'Week to date (Fri–Thu)'}</th>
                    {showOt && <th className={`${styles.th}`}>Overtime</th>}
                    <th className={`${styles.th} ${styles.thRight}`}>Wage</th>
                    <th className={`${styles.th}`}>Status</th>
                </tr>
            </thead>
            <tbody>
                {filteredData.map((item) => (
                    <DayViewRow key={item?.id} item={item} date={selectedDate} showOt={showOt} onOpenEmployee={onOpenEmployee} setIsModalOpen={setIsModalOpen} setIsNewModalOpen={setIsNewModalOpen} setSelectedEmp={setSelectedEmp} setSelectedDay={setSelectedDay} setEditData={setEditData} />
                ))}
            </tbody>
        </table>}

        {!contentLoading && filteredData?.length === 0 && <EmptyState />}

        {isModalOpen && (<EntryDetailModal setIsModalOpen={setIsModalOpen} selectedEmp={selectedEmp} selectedDay={selectedDay} deleteTimeTracking={deleteTimeTracking} setIsNewModalOpen={setIsNewModalOpen} setEditData={setEditData} />)}

        {isNewModalOpen && (<EntryFormModal setIsModalOpen={setIsNewModalOpen} selectedEmp={selectedEmp} selectedDay={selectedDay} setLoading={setLoading} updateTimeTracking={updateTimeTracking} editData={editData} />)}
    </div>);
}

export default DayViewTable;
